import axios from 'axios'
import aqe from '@pushly/aqe'
import type { PageTagScope } from './page-tag-source'

/** One page of a field's values, ordered by how many subscribers hold them. */
export interface SegmentFieldValueReadRequest {
    /** The field's key, as the catalog names it. */
    key: string
    /** Narrows the page to values starting with this. Absent reads the list unnarrowed. */
    search?: string
    /** How many values to skip. */
    offset: number
    /** How many values to read. */
    limit: number
    /** Abandons the read. A read that sees this raised must not resolve with results. */
    signal: AbortSignal
}

/**
 * Where a field's values are read from.
 *
 * An interface rather than a direct call for the same reason page tags have one: the levels address
 * different endpoints, and a caller can state either answer without a server.
 */
export interface SegmentFieldValueSource {
    read(request: SegmentFieldValueReadRequest): Promise<string[]>
}

/**
 * Where the level's field values are served.
 *
 * Resolved when a read happens rather than when the source is built, so merely having a source does
 * not depend on application configuration being in place yet.
 *
 * The key is escaped because it is a property name the domain chose, not something this code picked
 * — a custom property is free to contain characters a path segment gives meaning to.
 */
function valuesUrl(scope: PageTagScope, key: string): string {
    const field = encodeURIComponent(key)

    return scope.level === 'domain'
        ? `${aqe.defaults.publicApiDomain}/domains/${scope.domainId}/segment-fields/${field}/values`
        : `${aqe.defaults.publicApiDomain}/accounts/${scope.orgId}/segment-fields/${field}/values`
}

/**
 * Reads a level's values for one field, a page at a time.
 *
 * This is what a field whose catalog entry carries only part of its values — or none of it — is
 * searched through. Without it such a field renders a list that is silently empty or silently
 * partial, with no way to reach the value the reader is looking for.
 *
 * Matching at the far end is by prefix, so what comes back are the values *starting* with what was
 * typed rather than containing it.
 */
export function createSegmentFieldValueSource(scope: PageTagScope): SegmentFieldValueSource {
    return {
        async read({ key, search, offset, limit, signal }) {
            // The transport predates abort signals, so the signal is adapted onto the cancellation it
            // does understand. Both directions matter: a signal already raised must not start a
            // request that nothing will stop.
            const cancellation = axios.CancelToken.source()
            const stop = () => cancellation.cancel()

            if (signal.aborted) stop()
            else signal.addEventListener('abort', stop, { once: true })

            try {
                const { data } = await axios.get(valuesUrl(scope, key), {
                    cancelToken: cancellation.token,
                    // Omitted rather than sent empty where they carry no instruction: the endpoint
                    // reads a non-positive offset and a blank search as absent anyway, and sending
                    // them makes two identical requests look different in a log.
                    params: {
                        limit,
                        ...(offset > 0 ? { offset } : {}),
                        ...(search ? { search } : {}),
                    },
                })

                // An empty value is dropped along with anything that is not a string: it would render
                // as a blank row the reader cannot tell apart from a rendering fault, and cannot
                // usefully pick.
                return Array.isArray(data?.data)
                    ? data.data.filter(
                          (value: unknown): value is string => typeof value === 'string' && value.length > 0,
                      )
                    : []
            } finally {
                signal.removeEventListener('abort', stop)
            }
        },
    }
}
